import React, { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import { Alert, Box, SpaceBetween } from "@amzn/awsui-components-react";
import { QuickSightDashboard } from "./QuicksightDashboard";
import { DashboardStageSelector } from "./DashboardStageSelector";
import PageHeader from "../PageHeader";
import DarkModeContext from "../../contexes/DarkModeContext";
import { StageContext } from "../../contexes/OrderIdContext";
import { AnalyticsContext } from "./AnalyticsProvider";
import { SavedDashboardView } from "../../hooks/useSavedDashboardViews";
import { Stage } from "../../lusca-search-api/generated-src";
import { DARK_THEME } from "../../constants";

interface DashboardErrorBoundaryProps {
  recordError: (error: Error) => void;
  // React 18 types no longer include `children` implicitly on class components.
  children?: React.ReactNode;
}

interface DashboardErrorBoundaryState {
  hasError: boolean;
}

class DashboardErrorBoundary extends React.Component<
  DashboardErrorBoundaryProps,
  DashboardErrorBoundaryState
> {
  state: DashboardErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.log("Caught error: ", error, errorInfo);
    this.props.recordError(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box padding="l">
          <Alert type="error" header="Error" dismissible={false}>
            An unexpected error occurred. Please refresh the page.
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [darkMode, setDarkMode] = useState(
    () => window.localStorage.getItem("theme") === DARK_THEME
  );
  const stageParam = (searchParams.get("stage") as Stage) || Stage.Prod;
  const [shareHandler, setShareHandler] = useState<
    (() => Promise<string>) | null
  >(null);
  const [filtersHandler, setFiltersHandler] = useState<
    (() => Promise<string>) | null
  >(null);
  const [filterRequest, setFilterRequest] = useState<
    { filters: string | null; ts: number } | undefined
  >();
  const [activeViewName, setActiveViewName] = useState<string | null>(
    searchParams.get("view") || null
  );

  const handleEnvironmentChange = (environment: string) => {
    setSearchParams({ stage: environment });
    setActiveViewName(null);
  };

  const handleLoadView = (view: SavedDashboardView | null) => {
    if (!view) {
      setActiveViewName(null);
      setFilterRequest({ filters: null, ts: Date.now() });
      return;
    }
    setActiveViewName(view.name);
    if (view.stage !== stageParam) {
      setSearchParams({ stage: view.stage });
    }
    setFilterRequest({ filters: view.filters, ts: Date.now() });
  };

  const { recordError } = useContext(AnalyticsContext);

  return (
    <DashboardErrorBoundary recordError={recordError}>
      <DarkModeContext.Provider value={darkMode}>
        <StageContext.Provider value={stageParam}>
          <PageHeader setDarkMode={setDarkMode} />
          <AppLayout
            content={
              <SpaceBetween size="m">
                <Box padding={{ horizontal: "l", top: "m" }}>
                  <DashboardStageSelector
                    stage={stageParam}
                    setStage={handleEnvironmentChange}
                    getShareUrl={shareHandler}
                    getCompressedFilters={filtersHandler}
                    onLoadView={handleLoadView}
                    activeViewName={activeViewName}
                    setActiveViewName={setActiveViewName}
                  />
                </Box>
                <QuickSightDashboard
                  stage={stageParam}
                  dashboardType="dlq"
                  onLoad={() => console.log("Dashboard loaded")}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  setShareHandler={setShareHandler}
                  setFiltersHandler={setFiltersHandler}
                  filterRequest={filterRequest}
                  activeViewName={activeViewName}
                />
              </SpaceBetween>
            }
            contentType="default"
            headerSelector="#h"
            maxContentWidth={1700}
            navigationHide
            toolsHide
          />
        </StageContext.Provider>
      </DarkModeContext.Provider>
    </DashboardErrorBoundary>
  );
};

export default Dashboard;
